import React, { useState, useEffect } from 'react';
import { Title, Loading, useLogout } from 'react-admin';
import {
  API_URL, authHeaders,
  Card, StackedBarChart, StepTable, ErrorsList, Section, DaysSelector,
  STACK_KEYS, STACK_COLORS, STACK_LABELS,
} from './ProcessingStatsShared';

const TenderProcessingStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const logout = useLogout();

  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (!isLocal) return <div style={{ padding: 48, textAlign: 'center', color: '#888' }}>Brak dostępu.</div>;

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/tenders/processing-stats/?days=${days}`, { headers: authHeaders() })
      .then((r) => {
        if (r.status === 401) { logout(); return null; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => { if (d) { setData(d); setError(null); } })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <Loading />;
  if (error) return <div style={{ padding: 24, color: '#c62828' }}>Błąd: {error}</div>;
  if (!data) return null;

  const { summary, daily, tender_steps, tender_errors } = data;

  const chartData = daily.map((d) => ({
    date: d.date,
    done: d.tender_done || 0,
    failed: d.tender_failed || 0,
    waiting: d.tender_waiting || 0,
    running: d.tender_running || 0,
  }));

  const donePct = summary.tender_total
    ? Math.round(summary.tender_done / summary.tender_total * 100)
    : 0;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
      <Title title="Monitoring przetargów" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35em', color: '#333' }}>Monitoring przetargów</h2>
          <p style={{ color: '#888', margin: '4px 0 0', fontSize: '0.83em' }}>Dane operacyjne — widoczne tylko lokalnie</p>
        </div>
        <DaysSelector days={days} setDays={setDays} />
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
        <Card label="Przetargi łącznie" value={summary.tender_total} />
        <Card label="Ukończone" value={summary.tender_done} bg="#e8f5e9" color="#2e7d32" sub={`${donePct}%`} />
        <Card
          label="Błędy"
          value={summary.tender_failed}
          bg={summary.tender_failed > 0 ? '#ffebee' : undefined}
          color={summary.tender_failed > 0 ? '#c62828' : undefined}
        />
        <Card label="Oczekujące" value={summary.tender_waiting} bg="#fff8e1" color="#f57f17" />
      </div>

      {/* Daily chart */}
      <Section title="Aktywność dzienna — Przetargi">
        <StackedBarChart
          data={chartData}
          keys={STACK_KEYS}
          colors={STACK_COLORS}
          labels={STACK_LABELS}
        />
      </Section>

      {/* Step distribution */}
      <Section title="Rozkład kroków workflow">
        <StepTable steps={tender_steps} />
      </Section>

      {/* Errors */}
      <Section
        title={
          <>
            Ostatnie błędy
            {tender_errors.length > 0 && (
              <span style={{ marginLeft: 6, background: '#ffebee', color: '#c62828', borderRadius: 10, padding: '1px 8px', fontSize: '0.85em' }}>
                {tender_errors.length}
              </span>
            )}
          </>
        }
      >
        <ErrorsList errors={tender_errors} type="tender" />
      </Section>
    </div>
  );
};

export default TenderProcessingStats;
